import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  CreateVisitorDto,
  SportQuestionsResponseDto,
  SportResponseDto,
} from '@nomiss/sdk';
import {
  createVisitor,
  getPostSportDigestFlow,
  getSportQuestions,
  listSports,
  submitDigestPreferences,
} from '@nomiss/services';

export const onboardingQueryKeys = {
  sports: ['onboarding', 'sports'] as const,
  questions: (sport: string) => ['onboarding', 'questions', sport] as const,
  postSport: ['onboarding', 'post-sport'] as const,
};

export const sportsListQueryOptions = {
  queryKey: onboardingQueryKeys.sports,
  queryFn: () => listSports(),
};

export const postSportDigestQueryOptions = {
  queryKey: onboardingQueryKeys.postSport,
  queryFn: () => getPostSportDigestFlow(),
};

export function sportQuestionsQueryOptions(sport: string) {
  return {
    queryKey: onboardingQueryKeys.questions(sport),
    queryFn: () => getSportQuestions(sport),
  };
}

export const useSportQuestions = (
  sport: string,
  options?: { enabled?: boolean },
) => {
  const sportQuestionsQuery = useQuery<SportQuestionsResponseDto>({
    ...sportQuestionsQueryOptions(sport),
    ...options,
  });

  return { sportQuestionsQuery };
};

export const useSportsList = (options?: { enabled?: boolean }) => {
  const sportsListQuery = useQuery<Array<SportResponseDto>>({
    ...sportsListQueryOptions,
    ...options,
  });

  return { sportsListQuery };
};

export const usePostSportDigest = (options?: { enabled?: boolean }) => {
  const postSportDigestQuery = useQuery({
    ...postSportDigestQueryOptions,
    ...options,
  });

  return { postSportDigestQuery };
};

export type SubmitDigestPreferencesInput = {
  visitorId: string;
  displayName: string;
  selectedSports: string[];
  sportAnswers: Record<string, Record<string, string[]>>;
  streamingPlatforms?: string[];
  digestTimeCode: string;
  championDigestTimeCode?: string;
};

export const useSubmitDigestPreferences = () => {
  const submitMutation = useMutation({
    mutationFn: async ({
      visitorId,
      ...payload
    }: SubmitDigestPreferencesInput) =>
      submitDigestPreferences(visitorId, payload),
  });

  return { submitMutation };
};

export const useCreateVisitorSession = () => {
  const createVisitorMutation = useMutation({
    mutationFn: (dto: CreateVisitorDto) => createVisitor(dto),
  });

  return { createVisitorMutation };
};
